import { ImagePaths } from '../../lib/imageUrls.js';

export const purviewForCopilot = {

    id: 38,
    slug: 'purview-for-copilot',
    ogImage: '/og/courses/purview-for-copilot.jpg',
    courseCode: 'PURVIEW-COPILOT-001',
    featured: true,
    title: 'Purview for AI Governance (DSPM)',
    subtitle: 'Govern every Copilot variant and third-party AI with Data Security Posture Management for AI',
    valueProposition: 'Govern the full AI estate with Microsoft Purview — assess and remediate Copilot oversharing using DSPM for AI, govern every Copilot variant, integrate ChatGPT Enterprise and Azure OpenAI into one framework, and detect Shadow AI while meeting Australian privacy and security obligations.',
    duration: '8 hours',
    students: 'New',
    level: 'Advanced',
    image: ImagePaths.COURSE_2,
    shortDescription: 'PURVIEW-COPILOT-001 — Purview DSPM for AI covering Copilot governance, ChatGPT and Azure OpenAI, Shadow AI, and OAIC APP 8 for compliance leads.',
    price: {
      aud: 1200,
      currency: 'AUD'
    },
    stripeConfig: {
      priceId: 'price_1Te5C4Ku1nyc322UsD2W0ewH',
      productId: 'prod_UapCnmlXG8infp'
    },

    snapshot: {
      whoItsFor: [
        'Compliance officers operationalising DSPM for AI across Copilot variants and third-party AI tools',
        'Security architects designing AI guardrails across data, identity, prompts, and Shadow AI',
        'Microsoft 365 administrators governing Copilot, Copilot Studio, ChatGPT Enterprise, and Azure OpenAI',
        'Privacy and risk leads accountable for Australian Privacy Principle 8 cross-border disclosure'
      ],
      accreditation: 'Aligned to Microsoft Purview DSPM for AI, ACSC Copilot guidance (published October 2025 and still the current ACSC release), and ASD ISM — content beyond SC-401 certification scope'
    },

    logistics: {
      classSize: 'Live virtual cohort with hands-on Purview portal labs and Q&A',
      requirements: [
        'Working Microsoft 365 E5 or Copilot for Microsoft 365 tenant with Purview enabled',
        'Global Reader plus Compliance Administrator role for labs',
        'Familiarity with Microsoft 365 admin centres, sensitivity labels, and DLP basics',
        'Awareness of the Australian Privacy Act 1988 and ACSC Essential Eight context'
      ]
    },

    overview: 'Microsoft Purview is now the primary control surface for the entire enterprise AI estate — not just Microsoft 365 Copilot. Data Security Posture Management (DSPM) for AI consolidates signals across every Copilot variant (Microsoft 365 Copilot, Security Copilot, Copilot Studio, and Microsoft Fabric) and extends to third-party AI tools including ChatGPT Enterprise and Azure OpenAI. This one-day intensive walks compliance officers, security architects, and M365 administrators through comprehensive AI governance: assessing and remediating Copilot oversharing with DSPM for AI, designing SharePoint information architecture optimised for Copilot accuracy and safety, governing all Copilot variants, integrating ChatGPT Enterprise and Azure OpenAI into a unified governance framework, detecting unauthorised AI tool usage (Shadow AI) through Microsoft Defender for Cloud Apps, preventing prompt injection attacks, enforcing AI ethics policy, and documenting the Australian Privacy Principle 8 considerations triggered by Anthropic and other subprocessors in the AI supply chain. Market position: first Australian training covering comprehensive AI governance across all Copilot variants and third-party AI tools; content beyond SC-401 certification scope. Purview for AI Governance (DSPM) is Module 5 of 6 in the EDUC4TE Purview Training Track, focused on AI-specific data protection and complementing Module 4 (Insider Risk for behavioural signals).',

    outcomes: [
      'Assess and remediate Copilot oversharing using DSPM for AI, the AI Hub, and Activity Explorer',
      'Design SharePoint information architecture optimised for Copilot accuracy and safety',
      'Govern all Microsoft Copilot variants (Microsoft 365 Copilot, Security Copilot, Copilot Studio, and Fabric)',
      'Integrate ChatGPT Enterprise and Azure OpenAI into a unified AI governance framework',
      'Prevent prompt injection attacks and implement AI ethics policy enforcement',
      'Detect unauthorised AI tool usage (Shadow AI) through Microsoft Defender for Cloud Apps',
      'Document Australian Privacy Principle 8 cross-border disclosure analysis, including Anthropic subprocessor use, for executive sign-off'
    ],

    audience: [
      'Compliance officers and privacy leads',
      'Security architects and Microsoft 365 security engineers',
      'M365 administrators governing Copilot and third-party AI',
      'Risk, audit, and assurance practitioners',
      'Information governance and records managers'
    ],

    prerequisites: [
      'Module 2 of the EDUC4TE Purview Training Track (or equivalent Purview foundations)',
      'Hands-on experience with the Microsoft 365 admin centre',
      'Working knowledge of sensitivity labels and basic Purview DLP',
      'Familiarity with Australian privacy obligations under the Privacy Act 1988'
    ],

    modules: [
      {
        number: 1,
        title: 'Data Security Posture Management for AI',
        duration: '3 hours',
        topics: [
          'Assessing and remediating Microsoft 365 Copilot oversharing using DSPM for AI',
          'Activating the AI Hub and interpreting risk insights, recommendations, and prebuilt policies',
          'Investigating prompt and response events in Activity Explorer',
          'Designing SharePoint information architecture optimised for Copilot accuracy and safety',
          'Running an oversharing assessment and prioritising remediation'
        ]
      },
      {
        number: 2,
        title: 'Multi-Copilot governance',
        duration: '3 hours',
        topics: [
          'Governing all Microsoft Copilot variants — Microsoft 365 Copilot, Security Copilot, Copilot Studio, and Fabric',
          'Integrating ChatGPT Enterprise and Azure OpenAI into a unified governance framework',
          'Sensitivity label inheritance into Copilot outputs, citations, and downstream artefacts',
          'Copilot Studio agent governance: data sources, prompts, audit, and lifecycle controls',
          'Aligning agent and Copilot risk reviews to the ACSC Copilot guidance published in October 2025 — still the current ACSC release'
        ]
      },
      {
        number: 3,
        title: 'Shadow AI detection and AI security controls',
        duration: '2 hours',
        topics: [
          'Detecting unauthorised AI tool usage (Shadow AI) via Microsoft Defender for Cloud Apps',
          'Preventing prompt injection attacks against Copilot and third-party AI',
          'Implementing AI ethics policy enforcement across the AI estate',
          'APP 8 cross-border controls and Anthropic subprocessor disclosure',
          'Producing an APP 8 disclosure pack for the privacy officer and board'
        ]
      }
    ],

    format: {
      type: 'One-day intensive (virtual), or lunch-and-learn (8 x 1-hour sessions over 8 days)',
      includes: [
        '8 hours of practitioner-led training',
        'DSPM for AI and Copilot oversharing remediation templates',
        'Multi-Copilot and third-party AI governance framework',
        'Shadow AI detection and prompt injection prevention playbook',
        'APP 8 cross-border disclosure workbook',
        'Certificate of completion',
        'Lifetime access to course materials and updates'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Single intensive day, delivered in Australian business hours'
    },

    certification: 'Educ4te Purview for AI Governance (DSPM) Practitioner — certificate of completion suitable for CPD claims with ACS, ISACA, and (ISC)2. Content extends beyond SC-401 certification scope.',

    faqs: [
      {
        question: 'Does this course require a Microsoft 365 Copilot licence?',
        answer: 'A Microsoft 365 Copilot licence is recommended for the full lab experience, but the course covers governance of multiple AI tools — including ChatGPT Enterprise and Azure OpenAI. Without Copilot, you can still follow the DSPM for AI, Shadow AI, and policy authoring exercises in read-only mode.'
      },
      {
        question: 'How current is the AI governance content?',
        answer: 'The course reflects current Microsoft Purview DSPM for AI capabilities across all Copilot variants and third-party AI tools, plus the ACSC Copilot guidance published in October 2025, which remains the current ACSC release. We re-verify against Microsoft Learn and cyber.gov.au before every cohort and refresh the materials when either changes.'
      },
      {
        question: 'Will it cover Australian Privacy Principle 8 in detail?',
        answer: 'Yes. Module 3 walks through the OAIC interpretation of APP 8, maps the Microsoft and Anthropic subprocessor chain that underpins the AI supply chain, and produces a documented cross-border disclosure pack you can present to your privacy officer.'
      },
      {
        question: 'How is this different from a Copilot-only governance course?',
        answer: 'This course goes beyond Microsoft 365 Copilot to cover the entire AI estate: DSPM for AI, all Copilot variants (Security Copilot, Copilot Studio, Fabric), ChatGPT Enterprise and Azure OpenAI integration, Shadow AI detection through Defender for Cloud Apps, and prompt injection prevention. It is the first Australian training covering comprehensive AI governance across all Copilot variants and third-party AI tools, with content beyond SC-401 certification scope.'
      },
      {
        question: 'Is this aligned to ACSC and ASD guidance?',
        answer: 'Yes. Each module ties Purview and AI governance configuration choices back to the ACSC Copilot guidance issued in October 2025 — still the current release — and to relevant ASD Information Security Manual controls so that compliance evidence is reusable across frameworks.'
      },
      {
        question: 'Do you offer team or in-house delivery?',
        answer: 'Yes. We deliver private cohorts for compliance, security, and platform teams, with optional tenant-specific labs against your own Purview and AI configuration. Contact us for a tailored quote.'
      },
      {
        question: 'How does this fit into the EDUC4TE Purview Training Track?',
        answer: 'This is Module 5 of 6 in the EDUC4TE Purview Training Track. It focuses on AI-specific data protection — DSPM for AI, multi-Copilot governance, third-party AI integration, Shadow AI detection, and APP 8 controls. It pairs with Module 4 (Insider Risk and Communication Compliance) which covers behavioural signals around AI usage. Architects close out with Module 6 (Enterprise Architecture) which integrates DSPM unified, DSI, and Defender XDR. Prerequisite: Module 2.'
      },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
      }
    ],

    australianContext: true,
    complianceFrameworks: [
      'OAIC Australian Privacy Principle 8',
      'Privacy Act 1988',
      'ASD Information Security Manual',
      'ACSC Copilot guidance (published October 2025)'
    ]
};
